import React, { useState, useRef, useEffect } from "react";
import { Button, Typography, CircularProgress } from "@mui/material";
import { generateAudioTimestamp, triggerUploadCompletion } from './Audiorecordertab';
import axios from "axios";
import "./config/axiosConfig";
import { useSiteSettings } from './context/SiteSettingsContext';
import Portal from './components/Portal';
import ModalWrapper from './components/Modal/ModalWrapper';
import { useDialog } from './components/Modal/DialogContext';
import { Link } from "react-router-dom"; // Stelle sicher, dass NavLink importiert ist

import HeadphonesIcon from '@mui/icons-material/Headphones';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MicIcon from '@mui/icons-material/Mic';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ContentCutIcon from '@mui/icons-material/ContentCut';

type UploadDocumentsTabProps = {
  onUploadError?: (message: string) => void;
};

const UploadDocumentsTab: React.FC<UploadDocumentsTabProps> = ({ onUploadError }) => {
  const [isUpLoading, setIsUpLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false); // Zustand für die Dateivorschau
  const [originalName, setOriginalName] = useState(''); // Zustand für den Originalnamen
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const sessionIdRef = useRef<string>(''); // Ref für session_id
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const dialog = useDialog();
  const [showTipsDialog, setShowTipsDialog] = useState(false);





  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    setSelectedFiles(files);
    setShowPreview(true);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUpLoading(true);
    setUploadProgress(0);

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("file", file);
      formData.append("originalName", file.name);
    });

    try {
      await axios.post("/api/services/upload-documents/", formData, {
        onUploadProgress: progressEvent => {
          const percent = Math.round((progressEvent.loaded / (progressEvent.total ?? 1)) * 100);
          setUploadProgress(percent);
        }
      });
      setUploadProgress(100);
    } catch (err) {
      const message = "Das Hochladen der Dokumente hat leider nicht geklappt. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.";
      if (onUploadError) {
        onUploadError(message);
      } else {
        dialog({
          type: "alert",
          title: "Upload fehlgeschlagen",
          message,
          confirmButton: "Okay",
        });
      }
      setIsUpLoading(false);
      return;
    }

    setIsUpLoading(false);
    setSelectedFiles([]);
    setShowPreview(false);
    setUploadProgress(0);
  };


  return (
    <div className="tw-text-gray-500 tw-flex-1" style={{ display: 'grid', gridTemplateRows: '1fr auto', height: '100%' }}>

      {!showPreview && (
        <div data-state="active" data-orientation="horizontal" role="tabpanel" aria-labelledby="radix-113-trigger-file" id="radix-113-content-file" tabIndex={0}>
          <div role="presentation" tabIndex={0} className="tw-flex tw-h-full tw-flex-col  tw-text-center tw-relative">
            <div className="tw-mb-2">Für Erstgespräch, Verlaufsnotizen, Biografie, ... </div>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Unterstützte Formate: Derzeit ausschließlich reine Textdokumente (TXT)
            </Typography>
            <div className="tw-flex-1 tw-flex tw-items-center">
              <div className="tw-w-full">
                <div className="tw-flex tw-items-center tw-justify-center">
                  <input
                    type="file"
                    accept=".txt"
                    multiple
                    ref={inputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal !bg-[#E7A555]"
                  >
                    <div className="_btnFlex_19rto_32">
                      <div className="_btnAddon_19rto_56 _btnLeftAddon_19rto_61">
                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="tw-w-5">
                          <path d="M12.5 22C18.0228 22 22.5 17.5228 22.5 12C22.5 6.47715 18.0228 2 12.5 2C6.97715 2 2.5 6.47715 2.5 12C2.5 17.5228 6.97715 22 12.5 22Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                          <path d="M12.5 15.5V9.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                          <path d="M9.5 11.5L12.5 8.5L15.5 11.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                        </svg>
                      </div>
                      Dokument(e) auswählen
                    </div>
                  </button>
                </div>
                <div className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-mt-2">
                  <img className="tw-w-4" src="/dsgvo.png" alt="!" />
                  <span>100% DSGVO konform</span>
                  <div>
                    <button className="tw-flex" data-state="closed">
                      <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.00033 1.32487C11.9537 1.32487 15.167 4.5382 15.167 8.49154C15.167 12.4449 11.9537 15.6582 8.00033 15.6582C4.04699 15.6582 0.833658 12.4449 0.833658 8.49154C0.833658 4.5382 4.04699 1.32487 8.00033 1.32487ZM8.00033 14.6582C11.4003 14.6582 14.167 11.8915 14.167 8.49154C14.167 5.09154 11.4003 2.32487 8.00033 2.32487C4.60033 2.32487 1.83366 5.09154 1.83366 8.49154C1.83366 11.8915 4.60033 14.6582 8.00033 14.6582Z" fill="#8F8F8F"></path>
                        <path d="M8 7.32487C8.27333 7.32487 8.5 7.55154 8.5 7.82487V11.1582C8.5 11.4315 8.27333 11.6582 8 11.6582C7.72667 11.6582 7.5 11.4315 7.5 11.1582V7.82487C7.5 7.55154 7.72667 7.32487 8 7.32487Z" fill="#8F8F8F"></path>
                        <path d="M8.00033 5.15828C8.08699 5.15828 8.17366 5.17828 8.25366 5.21161C8.33366 5.24495 8.40699 5.29162 8.47366 5.35162C8.53366 5.41828 8.58033 5.48495 8.61366 5.57162C8.64699 5.65162 8.66699 5.73828 8.66699 5.82495C8.66699 5.91161 8.64699 5.99828 8.61366 6.07828C8.58033 6.15828 8.53366 6.23161 8.47366 6.29828C8.40699 6.35828 8.33366 6.40495 8.25366 6.43828C8.09366 6.50495 7.90699 6.50495 7.74699 6.43828C7.66699 6.40495 7.59366 6.35828 7.52699 6.29828C7.46699 6.23161 7.42033 6.15828 7.38699 6.07828C7.35366 5.99828 7.33366 5.91161 7.33366 5.82495C7.33366 5.73828 7.35366 5.65162 7.38699 5.57162C7.42033 5.48495 7.46699 5.41828 7.52699 5.35162C7.59366 5.29162 7.66699 5.24495 7.74699 5.21161C7.82699 5.17828 7.91366 5.15828 8.00033 5.15828Z" fill="#8F8F8F"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="tw-text-sm">
                  <div>Maximal Anzahl: 30 Dokumente</div>

                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPreview && (
        <div data-state="active" data-orientation="horizontal" role="tabpanel" aria-labelledby="radix-641-trigger-file" id="radix-641-content-file" tabIndex={0}>
          <div className="tw-flex tw-h-full tw-flex-col  tw-text-center tw-relative">
            <div className="tw-mb-2">Erstgespräch, Verlaufsnotizen, Biografie, ... </div>
            <div className="tw-flex-1 tw-flex tw-items-center">
              <div className="tw-w-full">
                <div className="_previews_1aueu_187">
                  <div className="_preview_16d6j_1">
                    <button className="_close_16d6j_15" onClick={() => {
                      setShowPreview(false);
                      setSelectedFiles([]);
                      setOriginalName('');
                    }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.00004 14.6666C11.6667 14.6666 14.6667 11.6666 14.6667 7.99992C14.6667 4.33325 11.6667 1.33325 8.00004 1.33325C4.33337 1.33325 1.33337 4.33325 1.33337 7.99992C1.33337 11.6666 4.33337 14.6666 8.00004 14.6666Z" stroke="#32768D" stroke-linecap="round" stroke-linejoin="round"></path>
                        <path d="M6.11328 9.88661L9.88661 6.11328" stroke="#32768D" stroke-linecap="round" stroke-linejoin="round"></path>
                        <path d="M9.88661 9.88661L6.11328 6.11328" stroke="#32768D" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    </button>
                    <div className="_nameWrapperFlex_16d6j_37">

                      <div className="_nameWrapper_16d6j_37">
                        <div className="_fileName_16d6j_51">
                          <div className="_firstChunk_16d6j_61">{originalName}</div>
                          <div className="_lastChunk_16d6j_66">Ausgewählte Dokumente</div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="tw-text-left tw-space-y-2"
                      style={{ maxHeight: '240px', overflowY: 'auto' }}
                    >
                      {selectedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="tw-flex tw-items-center tw-justify-between tw-border tw-rounded tw-p-2"
                        >
                          <span>{file.name}</span>
                          <button
                            className="tw-text-red-500"
                            onClick={() =>
                              setSelectedFiles(files => files.filter((_, i) => i !== idx))
                            }
                          >
                            Entfernen
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <button onClick={handleUpload} className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal sm:!tw-w-auto !tw-w-full !bg-[#E7A555]">
                  <div className="_btnFlex_19rto_32">
                    <div className="_btnAddon_19rto_56 _btnLeftAddon_19rto_61">
                      <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="tw-w-5">
                        <path d="M12.5 22C18.0228 22 22.5 17.5228 22.5 12C22.5 6.47715 18.0228 2 12.5 2C6.97715 2 2.5 6.47715 2.5 12C2.5 17.5228 6.97715 22 12.5 22Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M12.5 15.5V9.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M9.5 11.5L12.5 8.5L15.5 11.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                      </svg>
                    </div>
                    Dokument(e) hochladen
                  </div>
                </button>
              </div>
            </div>
            <div className="tw-text-sm">
              <div>Maximal Anzahl: 30 Dokumente</div>
            </div>
          </div>
        </div>
      )}
      {isUpLoading && (
        <div className="progress">
          <div className="progress__bar progress__bar--success" style={{ width: `${uploadProgress}%` }}>
            <div className="progress__bar__stripes"></div>
          </div>
          <div className="progress__status">
            <div className="tw-w-full progress__status mobile__pending">
              <div className="status note">
                <p>Upload... </p>
                {/*<p className="!tw-text-[#8F8F8F]">about 4 minutes left</p>*/}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="tab-tip tw-mt-1"><button className="btn-reset tw-text-primary-500 tw-underline" onClick={() => setShowTipsDialog(true)}><span>Tipps zum Dokumenten-Upload</span></button>
      </div>{showTipsDialog && (
        <Portal>
          <ModalWrapper onClose={() => setShowTipsDialog(false)} title="Tipps zum Upload">
            <div className="tw-grow tw-flex tw-flex-col">
              <ul className="sc-gEvEer fteAEG tw-grid tw-gap-4">
                <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                      <AssignmentTurnedInIcon fontSize="small" className="tw-max-w-full" />
                    </div>
                    <div>
                      Stellen Sie vor dem Upload sicher, dass Ihnen das Einverständnis Ihrer Klient:innen zur Nutzung von LumaNote und zur Verarbeitung der Aufnahme vorliegt. <Link to="/settings/clientconsent" className="sc-eqUAAy iEhnxW tw-text-primary-500 underline">Einverständnisformular herunterladen</Link>
                    </div>
                  </div>
                </li>
                <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                      <MicIcon fontSize="small" className="tw-max-w-full" />
                    </div>
                    <div><span className="tw-font-bold">Für ein optimales Ergebnis vermeiden Sie möglichst Störgeräusche, Pausen und Nebengespräche und achten Sie auf gleichmäßige Lautstärke sowie gut verständliche Stimmen.</span></div>
                  </div>
                </li>


                <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                      <ChatBubbleOutlineIcon fontSize="small" className="tw-max-w-full" />
                    </div>
                    <div>
                      Eine präzise Benennung von Interventionen und Fachbegriffen hilft LumaNote, die Qualität der Dokumentation zu verbessern.
                    </div>
                  </div>
                </li>

                <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                      <PlayCircleOutlineIcon fontSize="small" className="tw-max-w-full" />
                    </div>
                    <div>
                      Sie können nahezu alle gängigen Audioformate hochladen (z.&nbsp;B. MP3, WAV, M4A, AAC, FLAC).
                    </div>
                  </div>
                </li>
                <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                      <DeleteForeverIcon fontSize="small" className="tw-max-w-full" />
                    </div>
                    <div>
                      Hochgeladene Dateien werden am Ende des selben Tages automatisch gelöscht. <a target="_blank" rel="noreferrer" href="https://lumanote.de/security" className="sc-eqUAAy iEhnxW tw-text-primary-500 underline">Mehr erfahren</a>
                    </div>
                  </div>
                </li>

                <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                      <ContentCutIcon fontSize="small" className="tw-max-w-full" />
                    </div>
                    <div>
                      Laden Sie bei für jede Sitzungen getrennte Dateien hoch.
                    </div>
                  </div>
                </li>

              </ul>
            </div>
          </ModalWrapper>
        </Portal>
      )}</div>
  );
};

export default UploadDocumentsTab;
